// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppConfig } from "../../config/config";
import { httpStatus, SnackMessage } from "../../config/constant";
import { APIService } from "../../utils/apiService";
import {
  AddUserFeedbackRequestState,
  UserFeedbackInterface,
} from "../../utils/types";
import { enqueueSnackbarMessage } from "../commonSlice/common";

const initialState: AddUserFeedbackRequestState = {
  state: "idle",
  stateMessage: null,
  errorMessage: null,
  response: null,
  backgroundProcess: false,
  backgroundProcessMessage: null,
};

export const addUserFeedback = createAsyncThunk(
  "exams/userFeedback",
  async (userFeedback: UserFeedbackInterface, { dispatch }) => {
    return new Promise<{ userFeedback: any }>((resolve, reject) => {
      APIService.getInstance()
        .post(AppConfig.serviceUrls.submitFeedback, userFeedback)
        .then((resp) => {
          if (resp.status == 200) {
            dispatch(
              enqueueSnackbarMessage({
                message: SnackMessage.success.userFeedback,
                type: "success",
              })
            );
            resolve({ userFeedback: resp.data });
          } else {
            reject("Something went wrong");
          }
        })
        .catch((error: Error) => {
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.userFeedback,
              type: "error",
            })
          );
          reject(error);
        });
    });
  }
);

const userFeedbackSlice = createSlice({
  name: "userFeedback",
  initialState,
  reducers: {
    setState: (
      state,
      action: PayloadAction<AddUserFeedbackRequestState["state"]>
    ) => {
      state.state = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addUserFeedback.pending, (state, action) => {
        state.stateMessage = "Adding Feedback";
        state.state = "loading";
        state.backgroundProcess = true;
        state.backgroundProcessMessage = "Adding Feedback";
      })
      .addCase(addUserFeedback.fulfilled, (state, action) => {
        state.state = "success";
        state.backgroundProcess = false;
      })
      .addCase(addUserFeedback.rejected, (state, action) => {
        state.state = "failed";
        state.backgroundProcess = false;
      });
  },
});

export const { setState } = userFeedbackSlice.actions;
export default userFeedbackSlice.reducer;
